import { amplifyClient, getEndpointV2 } from 'api';
import { createApiSlice } from 'helpers/createApiSlice';
import { all, call, put, select } from 'redux-saga/effects';
import { UserData, UserRFC } from 'types';
import { UserRfcConfigConfiguration, UserRfcConfigType } from 'types';

import { rfcsFetch } from './rfcsFetch';
import { userFetch } from './userFetch';
import { addConfig, removeConfig, UpdateAddReturn, updateConfig } from './utils/rfcCertify';

export const needOnboarding = createApiSlice<Array<UserRFC>>({
  name: 'needOnboarding',
  *tryGenerator() {
    const { companyId } = (yield select(userFetch.selectors.getData)) as UserData;

    const { data } = yield call(amplifyClient, {
      method: 'get',
      url: getEndpointV2({ path: '/auth/company/:companyId/onBoarding', urlParams: { companyId } }),
    });

    return data;
  },
});

interface DataPayload {
  config: UserRfcConfigConfiguration;
  type: UserRfcConfigType;
  code: string;
}

type Payload = Array<DataPayload> | DataPayload;

export const rfcConfigAdd = createApiSlice<unknown, Payload>({
  name: 'rfcConfigAdd',
  *tryGenerator(_, { payload }) {
    const payloads: Array<DataPayload> = payload instanceof Array ? payload : [payload];

    const responses: Array<UpdateAddReturn> = yield all(
      payloads.map(({ config, type, code }) => call(addConfig, { config, type, code }))
    );

    const hasAllFailed = responses.every(({ error }) => !!error);
    if (!hasAllFailed) {
      yield put(rfcsFetch.actions.call());
    }

    const firstError = responses.find(({ error }) => !!error)?.error;

    if (firstError) {
      throw new Error(firstError.message);
    }

    return null;
  },
});

export const rfcConfigUpdate = createApiSlice<unknown, Payload>({
  name: 'rfcConfigUpdate',
  *tryGenerator(_, { payload }) {
    const payloads: Array<DataPayload> = payload instanceof Array ? payload : [payload];

    const responses: Array<UpdateAddReturn> = yield all(
      payloads.map(({ config, type, code }) => call(updateConfig, { config, type, code }))
    );

    const hasAllFailed = responses.every(({ error }) => !!error);
    if (!hasAllFailed) {
      yield put(rfcsFetch.actions.call());
    }

    const firstError = responses.find(({ error }) => !!error)?.error;

    if (firstError) {
      throw new Error(firstError.message);
    }

    return null;
  },
});

interface RemovePayload {
  type: UserRfcConfigType;
  code: string;
}

export const rfcConfigRemove = createApiSlice<unknown, RemovePayload>({
  name: 'rfcConfigRemove',
  *tryGenerator(_, { payload: { type, code } }) {
    yield call(removeConfig, { type, code });

    yield put(rfcsFetch.actions.call());

    return null;
  },
});
